<template>
    <div>
      <SettingHeaderTab />
  
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h5 class="page-title">Inventory Categories</h5>
          <p class="page-sub">Organise your products into categories</p>
        </div>
        <div class="page-actions">
  
          <div class="search-wrap">
            <font-awesome-icon icon="search" class="search-icon" />
            <input
              type="text"
              class="search-input"
              placeholder="Search categories..."
              v-model="query"
            />
          </div>
  
          <button
            class="btn-danger-outline"
            :class="{ 'btn--disabled': !canBulkDelete }"
            :disabled="!canBulkDelete"
            data-bs-toggle="modal"
            data-bs-target="#bulkModal"
          >
            <font-awesome-icon icon="trash" />
            Bulk Delete
            <span v-if="selectedModel.length > 0" class="bulk-count">{{ selectedModel.length }}</span>
          </button>
  
          <button
            class="btn-primary-action"
            :class="{ 'btn--disabled': permission.create_inventory_category != 1 }"
            :disabled="permission.create_inventory_category != 1"
            @click="openCreate"
          >
            <font-awesome-icon icon="plus" /> Create Category
          </button>
  
        </div>
      </div>
  
      <!-- Data card -->
      <div class="data-card">
  
        <!-- Loading -->
        <div v-if="loading" class="state-block">
          <div class="spinner-border text-primary" style="width:3rem;height:3rem;" role="status"></div>
          <p class="mt-3 text-muted-cell">Loading categories...</p>
        </div>
  
        <!-- 403 -->
        <div v-else-if="permission_status === 403" class="state-block">
          <div class="state-icon state-icon--red"><font-awesome-icon icon="lock" /></div>
          <p class="state-title">Access Denied</p>
          <p class="state-sub">You don't have permission to view categories.</p>
        </div>
  
        <!-- Empty -->
        <div v-else-if="categories.length === 0" class="state-block">
          <div class="state-icon state-icon--blue"><font-awesome-icon icon="tags" /></div>
          <p class="state-title">No categories found</p>
          <p class="state-sub">
            {{ query ? 'No results match your search.' : 'Create your first category to get started.' }}
          </p>
          <button
            v-if="!query && permission.create_inventory_category == 1"
            class="btn-primary-action mt-3"
            @click="openCreate"
          >
            <font-awesome-icon icon="plus" /> Create Category
          </button>
        </div>
  
        <template v-else>
  
          <!-- Toolbar -->
          <div class="table-toolbar">
            <span class="table-count">
              <strong>{{ categories.length }}</strong> categor{{ categories.length !== 1 ? 'ies' : 'y' }}
              <span v-if="selectedModel.length > 0" class="selected-hint">· {{ selectedModel.length }} selected</span>
            </span>
          </div>
  
          <!-- Table -->
          <div class="table-wrap">
            <table class="data-table">
              <thead>
                <tr>
                  <th class="col-check">
                    <input type="checkbox" class="row-check" v-model="allSelected" @change="selectAll" />
                  </th>
                  <th>Category Name</th>
                  <th class="col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="cat in categories"
                  :key="cat.id"
                  :class="{ 'row--selected': selectedModel.includes(cat.id) }"
                >
                  <td class="col-check">
                    <input type="checkbox" class="row-check" v-model="selectedModel" :value="cat.id" />
                  </td>
                  <td>
                    <div class="category-cell">
                      <div class="category-icon"><font-awesome-icon icon="tag" /></div>
                      <span class="category-name">{{ cat.category_name }}</span>
                    </div>
                  </td>
                  <td class="col-actions">
                    <div class="actions-cell">
                      <button
                        class="action-btn action-btn--edit"
                        :class="{ 'action-btn--disabled': permission.edit_inventory_category != 1 }"
                        :disabled="permission.edit_inventory_category != 1"
                        title="Edit"
                        @click="openEdit(cat)"
                      >
                        <font-awesome-icon icon="pen" />
                      </button>
                      <button
                        class="action-btn action-btn--delete"
                        :class="{ 'action-btn--disabled': permission.delete_inventory_category != 1 }"
                        :disabled="permission.delete_inventory_category != 1"
                        title="Delete"
                        @click="openDelete(cat)"
                      >
                        <font-awesome-icon icon="trash" />
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
  
        </template>
      </div>
  
      <!-- ── Create Modal ── -->
      <div class="modal-overlay" :class="{ open: createOpen }" @click.self="closeCreate">
        <div class="modal-box">
          <div class="modal-header-styled">
            <span class="modal-title-styled">Create Category</span>
            <button class="modal-close-btn" @click="closeCreate"><font-awesome-icon icon="times" /></button>
          </div>
          <form @submit.prevent="createCategory">
            <div class="modal-body-styled">
              <div class="form-group-styled">
                <label class="form-label-styled">Category Name <span class="req">*</span></label>
                <input
                  ref="createInput"
                  v-model="category_name"
                  type="text"
                  class="form-input-styled"
                  placeholder="e.g. Electronics, Apparel..."
                  required
                />
              </div>
            </div>
            <div class="modal-footer-styled">
              <button type="button" class="btn-ghost" @click="closeCreate">Cancel</button>
              <button type="submit" class="btn-primary-action" :disabled="!category_name.trim() || loading">
                <font-awesome-icon v-if="loading" icon="spinner" spin class="me-1" />
                {{ loading ? 'Creating...' : 'Create Category' }}
              </button>
            </div>
          </form>
        </div>
      </div>
  
      <!-- ── Edit Modal ── -->
      <div class="modal-overlay" :class="{ open: editOpen }" @click.self="closeEdit">
        <div class="modal-box">
          <div class="modal-header-styled">
            <span class="modal-title-styled">Edit Category</span>
            <button class="modal-close-btn" @click="closeEdit"><font-awesome-icon icon="times" /></button>
          </div>
          <form @submit.prevent="updateCategory">
            <div class="modal-body-styled">
              <div class="form-group-styled">
                <label class="form-label-styled">Category Name <span class="req">*</span></label>
                <input
                  v-model="category_name"
                  type="text"
                  class="form-input-styled"
                  placeholder="Enter category name"
                  required
                />
              </div>
            </div>
            <div class="modal-footer-styled">
              <button type="button" class="btn-ghost" @click="closeEdit">Cancel</button>
              <button type="submit" class="btn-primary-action" :disabled="!category_name.trim() || loading">
                <font-awesome-icon v-if="loading" icon="spinner" spin class="me-1" />
                {{ loading ? 'Saving...' : 'Save Changes' }}
              </button>
            </div>
          </form>
        </div>
      </div>
  
      <!-- Backdrop -->
      <div v-if="createOpen || editOpen" class="modal-backdrop-custom" />
  
      <!-- Delete modals (existing components) -->
      <SingleDeleteModal
        :modalId="'singleModal'"
        @deleteSingle="callSingleCategoryDeleteButton"
        :modalText="'Are you sure you want to delete this category? <strong>All subcategories assigned to it will also be deleted.</strong>'"
        :action="'deleteSingle'"
      />
      <BulkDeleteModal
        :modalId="'bulkModal'"
        @deleteBulk="callBulkCategoryDeleteButton"
        :modalText="'Are you sure you want to delete the selected categories? <strong>All subcategories assigned to them will also be deleted.</strong>'"
      />
  
    </div>
  </template>
  
  <script>
  import { ref, computed, watch, nextTick, onMounted } from 'vue';
  import { useStore } from 'vuex';
  import { useToast } from 'vue-toastification';
  import axios from 'axios';
  import SettingHeaderTab from './components/settingheadertab.vue';
  import SingleDeleteModal from '../../../components/ActionModal.vue';
  import BulkDeleteModal   from '../../../components/bulkDeleteModal.vue';
  
  export default {
    name: 'InventoryCategory',
  
    components: { SettingHeaderTab, SingleDeleteModal, BulkDeleteModal },
  
    setup() {
      const store = useStore();
      const toast = useToast();
  
      // ── State ────────────────────────────────────────────────────────
      const categories      = ref([]);
      const loading         = ref(false);
      const permission_status = ref(null);
      const query           = ref('');
      const category_name   = ref('');
      const category_id     = ref('');
      const selectedModel   = ref([]);
      const allSelected     = ref(false);
      const createOpen      = ref(false);
      const editOpen        = ref(false);
      const createInput     = ref(null);
  
      const permission   = computed(() => store.state.getCurrentUserPermission);
      const canBulkDelete = computed(() =>
        permission.value.delete_inventory_category == 1 && selectedModel.value.length > 0,
      );
  
      // ── Selection ────────────────────────────────────────────────────
      const selectAll = () => {
        selectedModel.value = allSelected.value ? categories.value.map(c => c.id) : [];
      };
  
      // ── Modal helpers ────────────────────────────────────────────────
      const openCreate = async () => {
        category_name.value = '';
        createOpen.value = true;
        await nextTick();
        createInput.value?.focus();
      };
  
      const closeCreate = () => { createOpen.value = false; category_name.value = ''; };
  
      const openEdit = (cat) => {
        category_id.value   = cat.id;
        category_name.value = cat.category_name;
        editOpen.value      = true;
      };
  
      const closeEdit = () => { editOpen.value = false; category_name.value = ''; category_id.value = ''; };
  
      const openDelete = (cat) => {
        category_id.value   = cat.id;
        category_name.value = cat.category_name;
        window.$('#singleModal').modal('show');
      };
  
      // ── CRUD ─────────────────────────────────────────────────────────
      const getAllCategories = async () => {
        loading.value = true;
        try {
          const { data } = await axios.get('settings/category', { params: { query: query.value } });
          categories.value = data.data;
        } catch (err) {
          permission_status.value = err?.response?.status;
          toast.error(err.response?.data?.message ?? 'Could not load categories.');
        } finally {
          loading.value = false;
        }
      };
  
      const createCategory = async () => {
        if (!category_name.value.trim()) return;
        loading.value = true;
        try {
          await axios.post('/settings/category', { category_name: category_name.value });
          toast.success('Category created successfully.');
          closeCreate();
          getAllCategories();
        } catch (err) {
          const status = err.response?.status;
          if (status === 422) toast.warning(err.response.data.message[0]);
          else if (status === 403) toast.error(err.response.data.message);
          else toast.error('An error occurred. Please try again.');
        } finally {
          loading.value = false;
        }
      };
  
      const updateCategory = async () => {
        if (!category_name.value.trim()) return;
        loading.value = true;
        try {
          await axios.put(`/settings/category/${category_id.value}`, { category_name: category_name.value });
          toast.success('Category updated successfully.');
          closeEdit();
          getAllCategories();
        } catch (err) {
          const status = err.response?.status;
          if (status === 422) toast.warning(err.response.data.message[0]);
          else if (status === 403) toast.error(err.response.data.message);
          else toast.error('An error occurred. Please try again.');
        } finally {
          loading.value = false;
        }
      };
  
      const callSingleCategoryDeleteButton = async () => {
        try {
          await axios.delete(`/settings/category/${category_id.value}`);
          toast.success('Category deleted successfully.');
          category_id.value = '';
          category_name.value = '';
          getAllCategories();
        } catch (err) {
          toast.error(err.response?.data?.message ?? 'Could not delete category.');
        }
      };
  
      const callBulkCategoryDeleteButton = async () => {
        try {
          const { data } = await axios.post('/settings/category/delete-bulk', { categories: selectedModel.value });
          toast.success(data.message);
          selectedModel.value = [];
          allSelected.value   = false;
          getAllCategories();
        } catch (err) {
          const status = err.response?.status;
          if (status === 422) toast.error(err.response.data.message[0]);
          else toast.error(err.response.data?.message ?? 'Bulk delete failed.');
        }
      };
  
      watch(query, () => getAllCategories());
      onMounted(getAllCategories);
  
      return {
        categories, loading, permission_status, query,
        category_name, category_id,
        selectedModel, allSelected, selectAll,
        createOpen, editOpen, createInput,
        openCreate, closeCreate, openEdit, closeEdit, openDelete,
        createCategory, updateCategory,
        callSingleCategoryDeleteButton, callBulkCategoryDeleteButton,
        permission, canBulkDelete,
      };
    },
  };
  </script>
  
  <style scoped>
  /* ── Page ────────────────────────────────────────────────────────────── */
  .page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:14px; margin-bottom:20px; }
  .page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
  .page-sub     { font-size:13px; color:#7a90b0; margin:0; }
  .page-actions { display:flex; align-items:center; flex-wrap:wrap; gap:8px; }
  
  /* ── Search ──────────────────────────────────────────────────────────── */
  .search-wrap  { position:relative; display:flex; align-items:center; }
  .search-icon  { position:absolute; left:11px; color:#7a90b0; font-size:13px; pointer-events:none; }
  .search-input { padding:8px 12px 8px 33px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; width:210px; outline:none; transition:border-color .15s; }
  .search-input:focus { border-color:#004AAD; }
  
  /* ── Buttons ─────────────────────────────────────────────────────────── */
  .btn-primary-action { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:8px 16px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; white-space:nowrap; }
  .btn-primary-action:hover:not(:disabled) { background:#003b8e; }
  .btn-primary-action:disabled, .btn--disabled { opacity:.45; pointer-events:none; cursor:not-allowed; }
  .btn-danger-outline { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#e03535; border:1px solid #fecaca; font-size:13px; font-weight:600; padding:8px 14px; border-radius:8px; cursor:pointer; transition:all .15s; white-space:nowrap; position:relative; }
  .btn-danger-outline:hover:not(:disabled) { background:#fff5f5; }
  .btn-danger-outline:disabled { opacity:.45; cursor:not-allowed; }
  .bulk-count { display:inline-flex; align-items:center; justify-content:center; background:#e03535; color:#fff; border-radius:10px; font-size:11px; font-weight:700; padding:1px 6px; margin-left:2px; }
  
  .btn-ghost { display:inline-flex; align-items:center; gap:7px; background:#f4f7fb; color:#3d5068; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; }
  .btn-ghost:hover { background:#e4eaf2; }
  .mt-3 { margin-top:12px; }
  .me-1 { margin-right:4px; }
  
  /* ── Data card ───────────────────────────────────────────────────────── */
  .data-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
  
  /* ── States ──────────────────────────────────────────────────────────── */
  .state-block      { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:64px 24px; text-align:center; }
  .state-icon       { width:60px; height:60px; border-radius:16px; display:flex; align-items:center; justify-content:center; font-size:24px; margin-bottom:16px; }
  .state-icon--blue { background:rgba(0,74,173,.08); color:#004AAD; }
  .state-icon--red  { background:#fff0f0; color:#e03535; }
  .state-title      { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
  .state-sub        { font-size:13px; color:#7a90b0; margin:0; }
  .text-muted-cell  { color:#4a5e78; }
  
  /* ── Table toolbar ───────────────────────────────────────────────────── */
  .table-toolbar  { display:flex; align-items:center; padding:14px 20px; border-bottom:1px solid #e4eaf2; }
  .table-count    { font-size:13px; color:#7a90b0; }
  .table-count strong { color:#1a2b45; }
  .selected-hint  { color:#004AAD; font-weight:600; }
  
  /* ── Table ───────────────────────────────────────────────────────────── */
  .table-wrap  { overflow-x:auto; }
  .data-table  { width:100%; border-collapse:collapse; }
  .data-table thead tr { background:#f8fafc; }
  .data-table thead th { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:12px 16px; border-bottom:1px solid #e4eaf2; white-space:nowrap; }
  .data-table tbody tr { border-bottom:1px solid #f0f4f8; transition:background .12s; }
  .data-table tbody tr:last-child { border-bottom:none; }
  .data-table tbody tr:hover { background:#f8fafc; }
  .data-table tbody tr.row--selected { background:#edf4ff; }
  .data-table tbody td { padding:12px 16px; font-size:13px; vertical-align:middle; }
  .col-check   { width:44px; padding-left:20px !important; }
  .col-actions { width:90px; text-align:center; }
  .row-check   { width:15px; height:15px; cursor:pointer; accent-color:#004AAD; }
  
  /* ── Category cell ───────────────────────────────────────────────────── */
  .category-cell { display:flex; align-items:center; gap:10px; }
  .category-icon { width:30px; height:30px; border-radius:7px; background:rgba(0,74,173,.08); color:#004AAD; display:flex; align-items:center; justify-content:center; font-size:12px; flex-shrink:0; }
  .category-name { font-size:13px; font-weight:500; color:#1a2b45; }
  
  /* ── Action buttons ──────────────────────────────────────────────────── */
  .actions-cell      { display:flex; align-items:center; justify-content:center; gap:6px; }
  .action-btn        { width:30px; height:30px; border-radius:7px; border:1px solid #dde5f0; background:#fff; display:flex; align-items:center; justify-content:center; font-size:12px; color:#7a90b0; cursor:pointer; transition:all .15s; }
  .action-btn--edit:hover:not(:disabled)   { background:#e8f4fd; color:#004AAD; border-color:#c5d6f5; }
  .action-btn--delete:hover:not(:disabled) { background:#fff0f0; color:#e03535; border-color:#f5c6c6; }
  .action-btn--disabled { opacity:.35; pointer-events:none; }
  
  /* ── Modal ───────────────────────────────────────────────────────────── */
  .modal-overlay         { position:fixed; inset:0; z-index:1050; display:flex; align-items:center; justify-content:center; opacity:0; pointer-events:none; transition:opacity .2s; }
  .modal-overlay.open    { opacity:1; pointer-events:all; }
  .modal-backdrop-custom { position:fixed; inset:0; z-index:1040; background:rgba(15,25,50,.45); backdrop-filter:blur(2px); }
  .modal-box             { background:#fff; border-radius:14px; width:100%; max-width:420px; margin:16px; box-shadow:0 20px 60px rgba(0,0,0,.18); overflow:hidden; transform:translateY(16px); transition:transform .22s ease; z-index:1051; position:relative; }
  .modal-overlay.open .modal-box { transform:translateY(0); }
  .modal-header-styled   { padding:20px 24px 16px; border-bottom:1px solid #e4eaf2; display:flex; align-items:center; justify-content:space-between; }
  .modal-title-styled    { font-size:16px; font-weight:700; color:#1a2b45; }
  .modal-close-btn       { width:30px; height:30px; border-radius:7px; border:1px solid #dde5f0; background:transparent; cursor:pointer; display:flex; align-items:center; justify-content:center; color:#7a90b0; font-size:13px; transition:background .15s; }
  .modal-close-btn:hover { background:#f4f7fb; }
  .modal-body-styled     { padding:20px 24px; }
  .modal-footer-styled   { padding:16px 24px; border-top:1px solid #e4eaf2; display:flex; justify-content:flex-end; gap:10px; }
  
  /* ── Form ────────────────────────────────────────────────────────────── */
  .form-group-styled  { display:flex; flex-direction:column; gap:6px; }
  .form-label-styled  { font-size:13px; font-weight:600; color:#1a2b45; }
  .req                { color:#e03535; }
  .form-input-styled  { width:100%; padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; outline:none; transition:border-color .15s, box-shadow .15s; font-family:inherit; }
  .form-input-styled:focus { border-color:#004AAD; box-shadow:0 0 0 3px rgba(0,74,173,.08); }
  
  /* ── Responsive ──────────────────────────────────────────────────────── */
  @media (max-width:768px) {
    .page-header, .page-actions { flex-direction:column; align-items:stretch; }
    .search-input { width:100%; }
  }
  </style>